/**
 * useFirebaseNotifications — real-time Firestore notification subscription.
 *
 * Signs into Firebase with the custom token the FinminOS backend mints
 * (same flow as useFirebaseChat) then subscribes to the current user's
 * `notifications/{userId}/items` collection, newest first.
 *
 * The Firestore custom-token UID equals the customer's UUID, which is also the
 * `{userId}` segment of the path — so `useAuth().user.id` is the correct key.
 *
 * `onNew` fires once per notification that arrives AFTER the initial snapshot,
 * so the provider can surface a toast without replaying history on load.
 */
import * as React from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  query,
  updateDoc,
  writeBatch,
  type DocumentData,
} from 'firebase/firestore';
import { firebaseAuth, firestoreDb } from '@/lib/firebase';
import { tokenStore } from '@/lib/api';
import type { AppNotification, NotificationStatus } from '../types';

const PAGE_SIZE = 50;

/** Normalise a raw Firestore doc into an AppNotification (defensive on field names). */
function toNotification(id: string, d: DocumentData): AppNotification {
  const createdRaw = d.created_at ?? d.createdAt;
  let createdAt: Date | null = null;
  if (createdRaw) {
    // Backend writes an ISO-8601 string; tolerate a Firestore Timestamp too.
    if (typeof createdRaw?.toDate === 'function') {
      createdAt = createdRaw.toDate();
    } else {
      const parsed = new Date(createdRaw);
      createdAt = Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  const rawData = d.data;
  const data: Record<string, string> =
    rawData && typeof rawData === 'object' ? (rawData as Record<string, string>) : {};

  return {
    id,
    title: String(d.title ?? ''),
    body: String(d.body ?? d.message ?? ''),
    event: String(d.event ?? ''),
    read: Boolean(d.read ?? d.is_read ?? false),
    createdAt,
    data,
  };
}

/** Newest first; docs missing a parseable timestamp sort to the bottom. */
function byCreatedAtDesc(a: AppNotification, b: AppNotification): number {
  return (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
}

interface UseFirebaseNotificationsOptions {
  userId: string | null | undefined;
  /** Called once per notification that arrives after the initial load. */
  onNew?: (n: AppNotification) => void;
}

export function useFirebaseNotifications({ userId, onNew }: UseFirebaseNotificationsOptions) {
  const [notifications, setNotifications] = React.useState<AppNotification[]>([]);
  const [status, setStatus] = React.useState<NotificationStatus>('idle');

  // Keep the latest onNew without forcing a re-subscribe.
  const onNewRef = React.useRef(onNew);
  React.useEffect(() => {
    onNewRef.current = onNew;
  }, [onNew]);

  React.useEffect(() => {
    if (!userId) {
      if (import.meta.env.DEV) console.warn('[notifications] no userId yet — not subscribing');
      return;
    }

    const firestoreToken = tokenStore.getFirestoreToken();
    if (!firestoreToken) {
      if (import.meta.env.DEV) {
        console.warn('[notifications] no firestore token in sessionStorage — sign in again');
      }
      return;
    }

    let mounted = true;
    let unsubscribe: (() => void) | null = null;
    const seen = new Set<string>();
    let initialized = false;

    setStatus('connecting');

    (async () => {
      try {
        await signInWithCustomToken(firebaseAuth, firestoreToken);
        if (!mounted) return;

        if (import.meta.env.DEV) {
          console.info(
            `[notifications] signed in to Firebase as uid=${firebaseAuth.currentUser?.uid} | ` +
              `querying notifications/${userId}/items`,
          );
        }

        // NOTE: intentionally NO server-side orderBy. A Firestore `orderBy` on a
        // field excludes every doc that lacks that field — if any notification
        // was written without `created_at`, it would silently vanish from the
        // result. We fetch by `limit` and sort newest-first on the client, which
        // surfaces all docs regardless of timestamp consistency.
        const itemsRef = collection(firestoreDb, 'notifications', userId, 'items');
        const q = query(itemsRef, limit(PAGE_SIZE));

        unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            if (!mounted) return;

            if (import.meta.env.DEV) {
              console.info(
                `[notifications] snapshot: ${snapshot.size} doc(s) at notifications/${userId}/items`,
              );
            }

            setNotifications(
              snapshot.docs
                .map((docSnap) => toNotification(docSnap.id, docSnap.data() ?? {}))
                .sort(byCreatedAtDesc),
            );
            setStatus('ready');

            if (!initialized) {
              // First snapshot is history — remember the ids, don't toast.
              snapshot.docs.forEach((docSnap) => seen.add(docSnap.id));
              initialized = true;
              return;
            }

            // Toast only for genuinely new, still-unread additions.
            snapshot.docChanges().forEach((change) => {
              if (change.type !== 'added' || seen.has(change.doc.id)) return;
              seen.add(change.doc.id);
              const n = toNotification(change.doc.id, change.doc.data() ?? {});
              if (!n.read) onNewRef.current?.(n);
            });
          },
          (err) => {
            if (!mounted) return;
            setStatus('error');
            // Surfaces e.g. "Missing or insufficient permissions" (rules) so the
            // real cause is visible instead of a silently-empty bell.
            console.error('[notifications] snapshot listener failed:', err);
          },
        );
      } catch (err) {
        if (!mounted) return;
        setStatus('error');
        console.error('[notifications] firebase sign-in failed:', err);
      }
    })();

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  }, [userId]); // firestoreToken is stable per session; userId drives (re)subscribe

  const markAsRead = React.useCallback(
    async (id: string) => {
      if (!userId) return;
      try {
        await updateDoc(doc(firestoreDb, 'notifications', userId, 'items', id), { read: true });
      } catch {
        // Best-effort — the live snapshot remains the source of truth.
      }
    },
    [userId],
  );

  const markAllAsRead = React.useCallback(async () => {
    if (!userId) return;
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return;
    try {
      const batch = writeBatch(firestoreDb);
      unread.forEach((n) =>
        batch.update(doc(firestoreDb, 'notifications', userId, 'items', n.id), { read: true }),
      );
      await batch.commit();
    } catch {
      // Best-effort.
    }
  }, [userId, notifications]);

  const unreadCount = React.useMemo(
    () => notifications.reduce((acc, n) => (n.read ? acc : acc + 1), 0),
    [notifications],
  );

  return { notifications, unreadCount, status, markAsRead, markAllAsRead };
}
